export type DismissalRecord = {
	readonly dismissalCount: number;
	readonly lastDismissedAtMs: number;
};

/**
 * Escalating retry table, one entry per past refusal, in milliseconds: 24 hours, 2 days, 7 days,
 * 30 days. Spacing grows because a single accidental tap deserves an immediate second chance,
 * while a repeated refusal is an increasingly deliberate signal that no further nudging helps.
 */
const RETRY_DELAYS_MS: readonly number[] = [
	24 * 60 * 60 * 1000,
	2 * 24 * 60 * 60 * 1000,
	7 * 24 * 60 * 60 * 1000,
	30 * 24 * 60 * 60 * 1000,
];

/**
 * Pure retry policy: given the dismissal record read from storage and the current instant,
 * decides whether the install banner may be offered now. No storage read, no clock read — this is
 * exactly what makes a thirty-day delay assertable in a test without waiting thirty days.
 *
 * No record (never dismissed, or a record `readInstallPromptDismissal` could not read) is always
 * eligible. Past the table's last entry — a 5th refusal onward — the banner is never spontaneously
 * offered again. A `lastDismissedAtMs` in the future (device clock changed) is treated as "just
 * dismissed": the elapsed time is clamped to zero rather than turned negative, which would
 * otherwise make the banner eligible immediately.
 */
const shouldOfferInstallPrompt = (dismissalRecord: DismissalRecord | null, now: Date): boolean => {
	if (dismissalRecord === null) {
		return true;
	}

	const tableIndex = dismissalRecord.dismissalCount - 1;
	const delayMs = RETRY_DELAYS_MS[tableIndex];

	if (delayMs === undefined) {
		return false;
	}

	const elapsedMs = Math.max(0, now.getTime() - dismissalRecord.lastDismissedAtMs);

	return elapsedMs >= delayMs;
};

export default shouldOfferInstallPrompt;
export { RETRY_DELAYS_MS };
